import type React from 'react';
import { useState, useEffect, useMemo } from 'react';
import { Box, Flex, Icon, Tooltip } from '@chakra-ui/react';
import { KText } from '@/ui-library/components/text';
import { KTag } from '@/ui-library/components/atoms/KTag';
import { KImage } from '@/ui-library/components/atoms/KImage';
import { CheckCircle } from '@kula-ai/icons/dual-tone/CheckCircle';
import { type Requisition } from '../associateReq.interface';
import { requisitionStatusConfig } from '../../constants/constants';
import { REQUISITION_TYPE_CONFIG } from '../../constants/enums';
import {
  PartOfGroupTag,
  GroupRequisitionCodes,
} from '@/components/requisitions/PartOfGroupTag';
import { TabBar } from '@/ui-library/components/atoms/KTabBar/KTabBar';

interface UserDisplayProps {
  avatar?: string;
  name?: string;
  label: string;
}

const UserDisplay: React.FC<UserDisplayProps> = ({ avatar, name, label }) => (
  <Flex alignItems="center" gap="6px">
    <KText size="sm-medium" color="secondary-900" width="fit-content">
      {label}
    </KText>
    {name ? (
      <Flex alignItems="center" gap="6px">
        <KImage src={avatar} name={name} />
        <KText>{name}</KText>
      </Flex>
    ) : (
      <KText> - </KText>
    )}
  </Flex>
);

interface StatusTagProps {
  status: string;
}

export const StatusTag: React.FC<StatusTagProps> = ({ status }) => {
  const statusConfig = requisitionStatusConfig[status];

  if (!status || !statusConfig) {
    return <KText>-</KText>;
  }

  return (
    <KTag
      color={statusConfig.color}
      bg="white"
      borderColor="border-color"
      borderWidth="1px"
      width="fit-content"
      value={
        <Flex alignItems="center" gap="6px">
          <Box w="8px" h="8px" bg={statusConfig.color} borderRadius="full" />
          <KText size="sm" color="secondary-700">
            {statusConfig.label}
          </KText>
        </Flex>
      }
    />
  );
};

interface RequisitionItemProps {
  req: Requisition;
  isSelected: boolean;
  recruiter?: { avatar?: string; name?: string };
  hiringManager?: { avatar?: string; name?: string };
  onSelect: (req: Requisition) => void;
  allRequisitions?: Requisition[];
  selectedReqs?: Requisition[];
  showGroupDialog?: boolean;
  onSelectAllGroup?: () => void;
  onSelectSingle?: () => void;
}

export const RequisitionItem: React.FC<RequisitionItemProps> = ({
  req,
  isSelected,
  recruiter,
  hiringManager,
  onSelect,
  allRequisitions = [],
  selectedReqs = [],
  showGroupDialog = false,
  onSelectAllGroup,
  onSelectSingle,
}) => {
  const [selectedOption, setSelectedOption] = useState<string>('');

  // Get all group requisitions for this req (allRequisitions already transformed by parent)
  const groupRequisitions = useMemo(() => {
    return allRequisitions?.filter(
      (r) =>
        r.requisition_group_id === req.requisition_group_id &&
        // eslint-disable-next-line eqeqeq
        r.requisition_group_id != null
    );
  }, [allRequisitions, req.requisition_group_id]);

  // Reset selection when dialog visibility changes
  useEffect(() => {
    if (!showGroupDialog) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- pre-existing; not refactoring during a mechanical import move
      setSelectedOption('');
    }
  }, [showGroupDialog]);

  // Check if all group requisitions are selected
  // If yes, set the 'yes' option; if not all are selected, reset it
  useEffect(() => {
    if (groupRequisitions.length > 0) {
      const allGroupSelected = groupRequisitions.every((groupReq) =>
        selectedReqs.some((selectedReq) => selectedReq.id === groupReq.id)
      );

      if (allGroupSelected && selectedOption !== 'yes') {
        // All group requisitions are selected, activate 'yes'
        // eslint-disable-next-line react-hooks/set-state-in-effect -- pre-existing; not refactoring during a mechanical import move
        setSelectedOption('yes');
      } else if (!allGroupSelected && selectedOption === 'yes') {
        // Not all are selected anymore, deactivate 'yes'
        setSelectedOption('');
      }
    }
  }, [selectedReqs, selectedOption, groupRequisitions]);

  const tabOptions = [
    { id: 'yes', label: 'Yes' },
    { id: 'no', label: 'No' },
  ];

  const handleTabChange = (id: string) => {
    setSelectedOption(id);
    if (id === 'yes') {
      onSelectAllGroup?.();
    } else if (id === 'no') {
      onSelectSingle?.();
    }
  };

  return (
    <Box
      py="12px"
      borderBottomWidth="1px"
      borderColor="border-color"
      cursor="pointer"
      bg={isSelected ? 'secondary-25' : 'white'}
      onClick={() => onSelect(req)}
      _hover={{ bg: 'secondary-25' }}
    >
      <Flex justifyContent="space-between" alignItems="flex-start" px="12px">
        <Flex width="100%" gap="3px" flexDir="column">
          <Flex alignItems="center" gap="6px">
            <KText size="sm-medium" color="secondary-900">
              {req?.code} - {req?.role_name}
            </KText>
            <StatusTag status={req?.status} />
            {req?.confidential && (
              <Tooltip
                label="The requisition associated with this job is confidential"
                placement="top"
                hasArrow
              >
                <Flex flexShrink={0}>
                  <KTag value="Confidential requisition" width="max-content" />
                </Flex>
              </Tooltip>
            )}
            <PartOfGroupTag
              requisition={req as any}
              allRequisitions={allRequisitions as any}
            />
          </Flex>

          <Flex>
            <KText>
              {req?.ats_department?.name} ·{' '}
              {req?.ats_offices?.length > 0
                ? req?.ats_offices[0]?.name
                : 'No office'}{' '}
              · {REQUISITION_TYPE_CONFIG[req?.requisition_type]?.label}
            </KText>
          </Flex>

          <Flex width="100%" flexWrap="wrap" gap="12px">
            <UserDisplay
              avatar={recruiter?.avatar}
              name={recruiter?.name}
              label="Recruitment partner"
            />
            <UserDisplay
              avatar={hiringManager?.avatar}
              name={hiringManager?.name}
              label="HM"
            />
          </Flex>

          {showGroupDialog && groupRequisitions.length > 1 && (
            <Box
              bg="white"
              p="12px"
              borderWidth="1px"
              borderColor="border-color"
              borderRadius="6px"
              onClick={(e) => e.stopPropagation()}
            >
              <Flex gap="6px" justifyContent="space-between">
                <KText size="sm-medium" color="secondary-900">
                  This requisition is part of a group. Do you want to associate
                  all the related requisitions?
                </KText>
                <TabBar
                  options={tabOptions}
                  selectedId={selectedOption}
                  onChange={handleTabChange}
                />
              </Flex>
              <GroupRequisitionCodes
                requisitions={groupRequisitions}
                gap="6px"
              />{' '}
            </Box>
          )}
        </Flex>

        <Box>{isSelected && <Icon as={CheckCircle} color="primary-500" />}</Box>
      </Flex>
    </Box>
  );
};
